import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Popover } from "@mui/material";
import { GearIcon, KeyboardIcon, SignOutIcon } from "@phosphor-icons/react";
import { useSidebarRevealHold } from "./SidebarContext";
import ViewerTimeZoneSelect from "./ViewerTimeZoneSelect";
import ClientEnvironmentMenu from "../ClientEnvironment/ClientEnvironmentMenu";
import ClientEnvironmentTimeLeft from "../ClientEnvironment/ClientEnvironmentTimeLeft";

/**
 * The signed-in person at the foot of the nav, and the account menu it opens:
 * the viewer's time zone (#11055), Settings for those who have it, and the way
 * out. It is the app's only profile control; the top-right profile icon every
 * page header used to carry folded into it, and the Settings row at the foot
 * did too, as in Claude's sidebar (Gerry: "put settings into the profile at
 * the bottom").
 *
 * In a client environment (#10718 slice 3) the organization named here is the
 * client, with the grant's time left beside it, and the menu holds what is
 * done to the grant, including leaving the client without logging out.
 *
 * Not the design system's `Menu`: that closes on a pick, and the time zone
 * picker here stays open while it is used. The paper wears the same surface
 * tokens. It opens above its trigger, leaving the avatar in view (Gerry's
 * call, over the footer-menu rule of covering the trigger), growing right
 * from the nav's left edge into the page.
 */
const SidebarAccount: React.FC<{
  initial: string;
  name: string;
  organizationName: string | null;
  /** The class that hides what only fits the wide panel. */
  wideOnly: string;
  /** Whether the nav is (or is becoming) the wide panel: the product rows'
   *  `shown || phone`. At rest the button is the rail tile they are. */
  wide: boolean;
  /** Where Settings lives, or null when the viewer has no Settings. */
  settingsPath: string | null;
  /** The page's keyboard shortcuts list, where the page has one (the claims
   *  screens, #11422 round 18), else undefined. */
  onShortcuts?: () => void;
}> = ({
  initial,
  name,
  organizationName,
  wideOnly,
  wide,
  settingsPath,
  onShortcuts,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The paper is portalled outside the panel: hold the hover reveal while it
  // is open, or opening it collapses the panel under it.
  useSidebarRevealHold(anchor !== null);

  return (
    <div
      data-nav-person=""
      className="flex-shrink-0 border-t border-white/10 pt-3"
    >
      <button
        type="button"
        aria-label="Account"
        aria-haspopup="dialog"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
        // Sized as the product rows are: a 36px tile on the rail, so its
        // hover is a square around the avatar rather than a band clipped by
        // the rail's edge, and the full row once the panel opens. The avatar
        // sits at the same x in both.
        className={`flex min-h-9 cursor-pointer items-center gap-2.5 overflow-hidden rounded-lg text-left transition-[background-color,width,margin-left,padding-left,padding-right] duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
          wide ? "ml-0 w-full px-1.5 py-1" : "ml-[2px] w-9 px-1"
        }`}
      >
        <span
          aria-hidden="true"
          className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-[var(--fp-accent)] text-[12px] font-semibold text-white"
        >
          {initial}
        </span>
        <span className={`min-w-0 flex-1 leading-tight ${wideOnly}`}>
          <span className="block truncate text-[13px] font-medium text-[#e6e4df]">
            {name}
          </span>
          {organizationName && (
            <span className="flex min-w-0 items-baseline gap-1.5 text-[12px] text-[#e6e4df]/55">
              <span className="truncate">{organizationName}</span>
              <ClientEnvironmentTimeLeft className="flex-shrink-0 whitespace-nowrap tabular-nums" />
            </span>
          )}
        </span>
      </button>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        marginThreshold={8}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Account",
            sx: {
              // A small gap above the avatar, which stays in view.
              marginTop: "-8px",
              width: 288,
              maxWidth: "calc(100vw - 16px)",
              borderRadius: "7px",
              border: "1px solid var(--fp-border)",
              backgroundColor: "var(--fp-surface)",
              boxShadow: "var(--fp-lift)",
              backgroundImage: "none",
            },
          },
        }}
      >
        <div className="border-b border-[var(--fp-border)] px-3 py-2.5 leading-tight">
          <div className="truncate text-[13px] font-medium text-[var(--fp-ink)]">
            {name}
          </div>
          {organizationName && (
            <div className="truncate text-[12px] text-[var(--fp-ink-2)]">
              {organizationName}
            </div>
          )}
        </div>
        <ViewerTimeZoneSelect />
        <ClientEnvironmentMenu />
        <div className="border-t border-[var(--fp-border)] p-1">
          {settingsPath && (
            <Link
              to={settingsPath}
              onClick={() => setAnchor(null)}
              className="flex h-[30px] w-full cursor-pointer items-center gap-2.5 rounded-[5px] px-2 text-[13px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fp-border-strong)]"
            >
              <GearIcon className="h-4 w-4" aria-hidden="true" />
              Settings
            </Link>
          )}
          {onShortcuts && (
            <button
              type="button"
              onClick={() => {
                setAnchor(null);
                onShortcuts();
              }}
              className="flex h-[30px] w-full cursor-pointer items-center gap-2.5 rounded-[5px] px-2 text-[13px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fp-border-strong)]"
            >
              <KeyboardIcon className="h-4 w-4" aria-hidden="true" />
              Keyboard shortcuts
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              window.location.href = "/logout";
            }}
            className="flex h-[30px] w-full cursor-pointer items-center gap-2.5 rounded-[5px] px-2 text-[13px] text-[var(--fp-danger)] hover:bg-[var(--fp-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fp-border-strong)]"
          >
            <SignOutIcon className="h-4 w-4" aria-hidden="true" />
            Log out
          </button>
        </div>
      </Popover>
    </div>
  );
};

export default SidebarAccount;
